import { useEffect, useState } from "react";
import { api, ApiError, isAuthed } from "./api";
import { pushToast, errorDetail } from "./toast";

/* Live operator data from the worker's super-admin reports. All use ?org=all for
   the cross-org (platform-wide) view. Return null until loaded / when signed out,
   so the God pages fall back to seed data. */

function useAdmin<T>(path: string): T | null {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<T>(path)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [path]);
  return data;
}

export type CostsReport = {
  totalCostUsd: number;
  byModel: { service: string; model: string; costUsd: number; calls: number }[];
};
export const useCosts = () => useAdmin<CostsReport>("/admin/costs?org=all");

export type TurnRow = {
  turnId: string; ts: number; userId: string; model: string; mode?: string;
  userInput?: string; assistantOutput?: string; hadScreenshot?: boolean;
  inputTokens: number; outputTokens: number; creditsCharged: number; cogsUsd?: number; marginUsd?: number;
};
export type TurnsReport = { turns: TurnRow[]; summary: { turns: number; marginPct: number | null } };
export const useTurns = () => useAdmin<TurnsReport>("/admin/turns?org=all&limit=12");

export type MarginReport = {
  blended: { turns: number; revenueUsd: number; cogsUsd: number; marginUsd: number; marginPct: number | null };
};
export const useMargin = () => useAdmin<MarginReport>("/admin/margin?org=all");

// ---- mapping helpers (worker shapes → the God page's display shapes) ----------

/** Provider/colour class from a model+service name (matches god.css .sp-dot.a-*). */
export function spendClass(service: string, model: string): string {
  const m = (model || "").toLowerCase();
  const s = (service || "").toLowerCase();
  if (s.includes("tts") || m.includes("cartesia") || m.includes("sonic")) return "a-tts";
  if (s.includes("stt") || m.includes("assembly")) return "a-stt";
  if (m.includes("opus")) return "a-opus";
  if (m.includes("sonnet") || m.includes("claude") || m.includes("haiku")) return "a-sonnet";
  return "a-cf";
}

export const fmtTokens = (n: number): string => (n >= 1000 ? (n / 1000).toFixed(1) + "k" : String(n));
export const shortUser = (id: string): string => (id && id.length > 10 ? id.slice(0, 8) + "…" : id || "—");
export const fmtClockUTC = (ts: number): string => new Date(ts).toISOString().slice(11, 19);

export const relTime = (ts: number): string => {
  if (!ts) return "—";
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};
const titleize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const nameFromEmail = (email: string) =>
  (email.split("@")[0] || "").split(/[._-]+/).filter(Boolean).map(titleize).join(" ") || email;
export const initialsFrom = (name: string) => {
  const p = name.split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || name.slice(0, 2).toUpperCase();
};

// ---- cross-org users (/admin/users) -----------------------------------------
export type AdminUser = {
  id: string; email: string; orgId: string; org: string; plan: string; role: string;
  turns: number; costUsd: number; marginPct: number | null; lastTs: number; mastery: string;
};
export const useUsers = () => useAdmin<{ users: AdminUser[] }>("/admin/users");

// ---- operator surfaces (audit, orgs, flags) ---------------------------------
export type AuditEntry = { who: string; action: string; target: string; reason: string; ts: number };
export const useAudit = () => useAdmin<{ entries: AuditEntry[] }>("/admin/audit");

export type AdminOrg = {
  id: string; name: string; tier: string; members: number; seats: string; seatLimit: number | null; createdAt: number;
  owner: string; domain: string; spendTodayUsd: number; marginPct: number | null; dailyCapUsd: number | null; sso: boolean;
};
export const useOrgs = () => useAdmin<{ orgs: AdminOrg[] }>("/admin/orgs");

export type FlagRow = { key: string; on: boolean; scope: string; desc: string };
export function useFlags(): { flags: FlagRow[]; setFlag: (key: string, on: boolean) => void } | null {
  const [flags, setFlags] = useState<FlagRow[] | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ flags: FlagRow[] }>("/admin/flags")
      .then((d) => alive && setFlags(d.flags))
      .catch(() => alive && setFlags(null));
    return () => {
      alive = false;
    };
  }, []);
  if (flags === null) return null;
  const setFlag = (key: string, on: boolean) => {
    setFlags((cur) => (cur ? cur.map((f) => (f.key === key ? { ...f, on } : f)) : cur)); // optimistic
    toastResult("/admin/flags", api("/admin/flags", { method: "POST", body: JSON.stringify({ key, on }) })).catch(() => {});
  };
  return { flags, setFlag };
}

// ---- enforced operator knobs + platform config (/admin/config) --------------
export type OperatorKnobs = { killSwitch: boolean; opusEnabled: boolean; globalDailyCapUsd: number; orgDailyCapUsd: number };
export type SelectorWeights = { leverage: number; readiness: number; fit: number; interleave: boolean };
export type ProviderProbes = { anthropic: boolean; cartesia: boolean; assemblyai: boolean };
export type AdminConfig = OperatorKnobs & {
  selector: SelectorWeights;
  cartesiaVoiceId: string;
  cartesiaModel: string;
  probes: ProviderProbes;
};

const KNOB_KEY: Record<string, keyof OperatorKnobs> = {
  kill_switch: "killSwitch", opus_enabled: "opusEnabled", global_daily_cap_usd: "globalDailyCapUsd", org_daily_cap_usd: "orgDailyCapUsd",
};

/** The full operator config. `knobs` keeps the original 4-key shape (Cost page);
 *  `platform` exposes the editable selector / voice / probe surface. `setKnob`
 *  optimistically flips the 4 knobs; `setConfig` writes any key then reloads. */
export function useConfig():
  | { knobs: OperatorKnobs; platform: AdminConfig; setKnob: (key: string, value: boolean | number) => void; setConfig: (key: string, value: boolean | number | string) => Promise<void>; reload: () => void }
  | null {
  const [cfg, setCfg] = useState<AdminConfig | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<AdminConfig>("/admin/config")
      .then((d) => alive && setCfg(d))
      .catch(() => alive && setCfg(null));
    return () => { alive = false; };
  }, [nonce]);
  if (cfg === null) return null;
  const knobs: OperatorKnobs = { killSwitch: cfg.killSwitch, opusEnabled: cfg.opusEnabled, globalDailyCapUsd: cfg.globalDailyCapUsd, orgDailyCapUsd: cfg.orgDailyCapUsd };
  const setKnob = (key: string, value: boolean | number) => {
    const k = KNOB_KEY[key];
    if (k) setCfg((cur) => (cur ? { ...cur, [k]: value } : cur)); // optimistic
    toastResult("/admin/config", api("/admin/config", { method: "POST", body: JSON.stringify({ key, value }) })).catch(() => {});
  };
  const setConfig = (key: string, value: boolean | number | string) =>
    toastResult("/admin/config", api("/admin/config", { method: "POST", body: JSON.stringify({ key, value }) })).then(() => setNonce((n) => n + 1)).catch(() => {});
  return { knobs, platform: cfg, setKnob, setConfig, reload: () => setNonce((n) => n + 1) };
}

// ---- curriculum authoring (lessons, tracks, assignment) ---------------------
export type AdminLesson = { id: string; type: string; title: string; objective: string; difficulty: number; status: string; template?: string; durationMinutes?: number | null; asksTimeSaved?: boolean; teachMistake?: boolean };
export const useLessonsAdmin = () => useAdmin<{ lessons: AdminLesson[] }>("/admin/lessons");

/** Reloadable catalog (for the editable Lessons tab). */
export function useLessonsCatalog(): { lessons: AdminLesson[] | null; reload: () => void } {
  const [lessons, setLessons] = useState<AdminLesson[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ lessons: AdminLesson[] }>("/admin/lessons")
      .then((d) => alive && setLessons(d.lessons))
      .catch(() => alive && setLessons(null));
    return () => {
      alive = false;
    };
  }, [nonce]);
  return { lessons, reload: () => setNonce((n) => n + 1) };
}

export type TrackRow = { id: string; name: string; description: string; icon: string; role: string; status: string; lessonCount: number; lessonIds: string[] };
export function useTracks(): { tracks: TrackRow[] | null; reload: () => void } {
  const [tracks, setTracks] = useState<TrackRow[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ tracks: TrackRow[] }>("/admin/tracks")
      .then((d) => alive && setTracks(d.tracks))
      .catch(() => alive && setTracks(null));
    return () => {
      alive = false;
    };
  }, [nonce]);
  return { tracks, reload: () => setNonce((n) => n + 1) };
}

export const saveTrack = (track: { id?: string; name: string; description?: string; icon?: string; role?: string }, lessonIds: string[]) =>
  post("/admin/tracks", { track, lessonIds });
export const deleteTrackReq = (trackId: string) => post("/admin/track-delete", { trackId });
export const assignItemReq = (itemType: "track" | "lesson", itemId: string, scope: "user" | "org", targetId: string) =>
  post("/admin/assign", { itemType, itemId, scope, targetId });
export const setUserMode = (userId: string, mode: "open" | "assigned") => post("/admin/user-mode", { userId, mode });
export const setLessonState = (userId: string, lessonId: string, state: "mastered" | "reset") =>
  post("/admin/lesson-state", { userId, lessonId, state });
export const saveLesson = (lesson: Partial<AdminLesson> & { title: string; type: string }) => post("/admin/lessons", { lesson });

// ---- operator mutations (audited server-side) -------------------------------

/** Per-action toast copy. ok = success line; fail = error prefix (detail appended). */
const ACTION_MSG: Record<string, { ok: string; fail: string }> = {
  "/admin/grant": { ok: "Credits granted", fail: "Grant failed" },
  "/admin/change-plan": { ok: "Plan updated", fail: "Plan change failed" },
  "/admin/suspend": { ok: "User suspended", fail: "Suspend failed" },
  "/admin/gdpr-delete": { ok: "User data deleted", fail: "Delete failed" },
  "/admin/impersonate": { ok: "Impersonation session started", fail: "Impersonation failed" },
  "/admin/revoke-session": { ok: "Session revoked", fail: "Revoke failed" },
  "/admin/revoke-sessions": { ok: "All sessions revoked", fail: "Revoke failed" },
  "/admin/reset-onboarding": { ok: "Onboarding reset", fail: "Reset failed" },
  "/admin/resend-invite": { ok: "Invite resent", fail: "Resend failed" },
  "/admin/org-config": { ok: "Org setting saved", fail: "Save failed" },
  "/admin/user-mode": { ok: "Curriculum mode updated", fail: "Update failed" },
  "/admin/lesson-state": { ok: "Lesson updated", fail: "Update failed" },
  "/admin/set-member-role": { ok: "Role updated", fail: "Couldn’t change role" },
  "/admin/memory-delete": { ok: "Memory deleted", fail: "Delete failed" },
  "/admin/tracks": { ok: "Track saved", fail: "Save failed" },
  "/admin/track-delete": { ok: "Track deleted", fail: "Delete failed" },
  "/admin/assign": { ok: "Assigned", fail: "Assign failed" },
  "/admin/unassign": { ok: "Track unassigned", fail: "Unassign failed" },
  "/admin/lessons": { ok: "Lesson saved", fail: "Save failed" },
  "/admin/flags": { ok: "Flag updated", fail: "Flag update failed" },
  "/admin/config": { ok: "Config saved", fail: "Config save failed" },
  "/admin/broadcast": { ok: "Broadcast sent", fail: "Broadcast failed" },
  "/admin/org-catalog-set": { ok: "Catalog updated", fail: "Update failed" },
};

/** Toasts the outcome of any operator mutation (success + failure). */
export async function toastResult<T>(path: string, p: Promise<T>): Promise<T> {
  const m = ACTION_MSG[path] ?? { ok: "Done", fail: "Action failed" };
  try {
    const r = await p;
    pushToast("success", m.ok);
    return r;
  } catch (e) {
    pushToast("error", `${m.fail}: ${errorDetail(e)}`);
    throw e;
  }
}

const post = (path: string, body: unknown) =>
  toastResult(path, api(path, { method: "POST", body: JSON.stringify(body) }));
export const grantCredits = (orgId: string, amount: number, reason: string, target: string) =>
  post("/admin/grant", { orgId, amount, reason, target });
// Live plan catalog (prices) from Autumn, for the change-plan modal.
export type AdminPlan = { id: string; name: string; amount: number | null; interval: string | null; display: string | null };
export const usePlans = () => useAdmin<{ plans: AdminPlan[] }>("/admin/plans");

export type BillingMode = "charge" | "comp" | "invoice";
export type ChangePlanResult = { ok: boolean; applied?: boolean; pending?: boolean; checkoutUrl?: string; invoiceUrl?: string | null; error?: string };

/** Change an org's plan. For paid tiers `mode` decides how Autumn bills:
 *  charge (card / hosted checkout), comp (free grant), or invoice (net terms).
 *  Returns a structured result so the caller can surface a checkout link when the
 *  customer has no card on file (charge mode → pending). Toasts the outcome. */
export async function changePlan(
  orgId: string, tier: string, reason: string, target: string,
  mode: BillingMode = "charge", netTermsDays?: number,
): Promise<ChangePlanResult> {
  const t = tier.toLowerCase();
  const isPaid = t === "pro" || t === "team";
  try {
    const r = await api<ChangePlanResult>("/admin/change-plan", {
      method: "POST",
      body: JSON.stringify({ orgId, tier: t, reason, target, mode, netTermsDays }),
    });
    pushToast("success",
      !isPaid ? "Plan updated"
      : mode === "comp" ? `Comped to ${tier}`
      : mode === "invoice" ? `${tier} active — invoice sent`
      : "Plan updated");
    return { ...r, ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.status === 402) {
      let body: ChangePlanResult = { ok: false };
      try { body = JSON.parse(e.body); } catch { /* ignore */ }
      pushToast("info", "Payment needed — copy the checkout link to send the customer.");
      return { ok: false, pending: true, checkoutUrl: body.checkoutUrl, error: body.error };
    }
    pushToast("error", `Plan change failed: ${errorDetail(e)}`);
    return { ok: false, error: errorDetail(e) };
  }
}
export const setMemberRole = (userId: string, role: "admin" | "member", target: string) =>
  post("/admin/set-member-role", { userId, role, target });
export const suspendUser = (userId: string, reason: string, target: string) =>
  post("/admin/suspend", { userId, reason, target });
export const gdprDelete = (userId: string, reason: string, target: string) =>
  post("/admin/gdpr-delete", { userId, reason, target });
export const impersonateUser = (userId: string, reason: string, target: string) =>
  post("/admin/impersonate", { userId, reason, target }) as Promise<{ access: string; refresh: string; email: string }>;

// ---- per-user detail (lessons, activity) ------------------------------------
export type UserLesson = { lessonId: string; title: string; type: string; status: string; fadeLevel: string | null; durationMinutes: number | null };
export const useUserLessons = (userId: string) =>
  useAdmin<{ items: UserLesson[]; retired: number; total: number }>(`/admin/user-lessons?user=${encodeURIComponent(userId)}`);

export type ActivityEvent = { ts: number; kind: string; icon: string; title: string; detail: string };
export const useUserActivity = (userId: string) =>
  useAdmin<{ events: ActivityEvent[] }>(`/admin/activity?user=${encodeURIComponent(userId)}`);

// ---- per-user path + conversation (read-only inspection) --------------------
export type PathLessonView = { lessonId: string; title: string; type: string; status: string; fadeLevel: string | null; durationMinutes: number | null; source?: string };
export type PathTrackView = { id: string; name: string; icon: string; description: string; source: string; lessons: PathLessonView[]; mastered: number; total: number };
export const useUserPath = (userId: string) =>
  useAdmin<{ tracks: PathTrackView[]; lessons: PathLessonView[] }>(`/admin/user-path?user=${encodeURIComponent(userId)}`);

export type ThreadView = { id: string; mode: string; lessonId: string | null; fadeLevel: string | null; title: string; status: string; turns: number; lastMessageAt: number };
export const useUserThreads = (userId: string) =>
  useAdmin<{ threads: ThreadView[] }>(`/admin/user-threads?user=${encodeURIComponent(userId)}`);

export type ThreadTurn = { user: string; assistant: string; ts: number };
export const fetchAdminThread = (userId: string, threadId: string) =>
  api<{ thread: ThreadView; turns: ThreadTurn[] }>(`/admin/thread?user=${encodeURIComponent(userId)}&id=${encodeURIComponent(threadId)}`);

// ---- per-org catalog approval (which tracks/lessons an org's learners see) ---
export type OrgCatalogTrack = { id: string; name: string; icon: string; lessonCount: number; visible: boolean };
export type OrgCatalogLesson = { id: string; title: string; type: string; visible: boolean };
export const useOrgCatalog = (orgId: string) =>
  useAdmin<{ tracks: OrgCatalogTrack[]; lessons: OrgCatalogLesson[] }>(`/admin/org-catalog?org=${encodeURIComponent(orgId)}`);
export const setOrgCatalogItem = (org: string, itemType: "track" | "lesson", itemId: string, visible: boolean) =>
  post("/admin/org-catalog-set", { org, itemType, itemId, visible });

// ---- sessions ---------------------------------------------------------------
export type Session = { id: string; device: string; ip: string; userAgent: string; createdAt: number; lastSeen: number; revoked: boolean };
export function useSessions(userId: string): { sessions: Session[] | null; reload: () => void } {
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ sessions: Session[] }>(`/admin/sessions?user=${encodeURIComponent(userId)}`)
      .then((d) => alive && setSessions(d.sessions))
      .catch(() => alive && setSessions(null));
    return () => { alive = false; };
  }, [userId, nonce]);
  return { sessions, reload: () => setNonce((n) => n + 1) };
}
export const revokeSession = (sessionId: string) => post("/admin/revoke-session", { sessionId });
export const revokeAllSessions = (userId: string, reason: string) => post("/admin/revoke-sessions", { userId, reason });
export const resetOnboarding = (userId: string, reason: string) => post("/admin/reset-onboarding", { userId, reason });
export const resendInvite = (userId: string) => post("/admin/resend-invite", { userId });

// ---- per-org config (seat limit, daily cap, retention) ----------------------
export type OrgConfig = { seatLimit: number | null; dailyCapUsd: number | null; retention: string | null; planSeats: number | null; memberCount: number };
export function useOrgConfig(orgId: string): { config: OrgConfig | null; reload: () => void } {
  const [config, setConfig] = useState<OrgConfig | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<OrgConfig>(`/admin/org-config?org=${encodeURIComponent(orgId)}`)
      .then((d) => alive && setConfig(d))
      .catch(() => alive && setConfig(null));
    return () => { alive = false; };
  }, [orgId, nonce]);
  return { config, reload: () => setNonce((n) => n + 1) };
}
export const setOrgConfig = (orgId: string, key: string, value: number | string | null) => post("/admin/org-config", { orgId, key, value });

// ---- per-org detail (assigned tracks) ---------------------------------------
export type OrgTrack = { id: string; name: string; icon: string; description: string; lessonCount: number; members: number; completionPct: number | null; assignedAt: number };
export const useOrgTracks = (orgId: string) =>
  useAdmin<{ tracks: OrgTrack[]; memberCount: number }>(`/admin/org-tracks?org=${encodeURIComponent(orgId)}`);

// ---- comms (broadcasts) -----------------------------------------------------
export type BroadcastRow = { who: string; subject: string; detail: string; ts: number };
export function useComms(): { broadcasts: BroadcastRow[] | null; emailConfigured: boolean; reload: () => void } {
  const [data, setData] = useState<{ broadcasts: BroadcastRow[]; emailConfigured: boolean } | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ broadcasts: BroadcastRow[]; emailConfigured: boolean }>("/admin/comms")
      .then((d) => alive && setData(d))
      .catch(() => alive && setData({ broadcasts: [], emailConfigured: false }));
    return () => { alive = false; };
  }, [nonce]);
  return { broadcasts: data?.broadcasts ?? null, emailConfigured: data?.emailConfigured ?? false, reload: () => setNonce((n) => n + 1) };
}
export type BroadcastResult = { recipients: number; inApp: number; emailsSent: number; emailsSkipped: number; emailsFailed: number; emailConfigured: boolean };
export const sendBroadcast = (payload: { audience: string; channel: string; subject: string; body: string }) =>
  toastResult("/admin/broadcast", api("/admin/broadcast", { method: "POST", body: JSON.stringify(payload) })) as Promise<BroadcastResult>;

// ---- desktop releases (Sparkle appcast) -------------------------------------
export type Release = {
  shortVersion: string; build: string; pubDateMs: number; minimumSystemVersion: string | null;
  url: string | null; sizeBytes: number; signed: boolean; releaseNotes: string | null;
};
export type ReleasesReport = { reachable: boolean; appcastUrl: string; channel: string; releases: Release[]; error?: string };
export const useReleases = () => useAdmin<ReleasesReport>("/admin/releases");
export const fmtBytes = (n: number): string => (n >= 1e6 ? (n / 1e6).toFixed(1) + " MB" : n >= 1e3 ? Math.round(n / 1e3) + " KB" : `${n} B`);

// ---- rate card (live cost rates from rates.ts) ------------------------------
export type RateCard = {
  rateCard: { item: string; rate: string }[];
  creditWeights: { model: string; weight: number }[];
  creditSalePriceUsd: number;
};
export const useRateCard = () => useAdmin<RateCard>("/admin/ratecard");

// ---- revenue board (plan mix × Autumn prices) -------------------------------
export type RevenueReport = {
  configured: boolean; mrrUsd: number | null; arpuUsd: number | null;
  payingOrgs: number; totalOrgs: number; newOrgsThisMonth: number;
  planMix: { tier: string; label: string; orgs: number; users: number; priceUsd: number | null }[];
};
export const useRevenue = () => useAdmin<RevenueReport>("/admin/revenue");

// ---- invoices passthrough (operator view of Autumn/Stripe invoices) ---------
export type Invoice = { id: string; status: string; total: number; currency: string; createdAt: number; url: string | null };
/** Pass exactly one of { org } or { user }. Returns null while loading. */
export function useInvoices(q: { org?: string; user?: string }): { invoices: Invoice[] | null; configured: boolean } {
  const [state, setState] = useState<{ invoices: Invoice[] | null; configured: boolean }>({ invoices: null, configured: true });
  const key = q.org ? `org=${q.org}` : q.user ? `user=${q.user}` : "";
  useEffect(() => {
    if (!isAuthed() || !key) return;
    let alive = true;
    api<{ configured: boolean; invoices: Invoice[] }>(`/admin/invoices?${key}`)
      .then((d) => alive && setState({ invoices: d.invoices, configured: d.configured }))
      .catch(() => alive && setState({ invoices: [], configured: true }));
    return () => { alive = false; };
  }, [key]);
  return state;
}
export const fmtMoney = (totalMinor: number, currency = "usd"): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format((totalMinor || 0) / 100);
export const fmtInvoiceDate = (ts: number): string => (ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "—");

// ---- observability + provider health ----------------------------------------
export type ObsReport = {
  requestVolume: { ts: number; turns: number }[];
  turnsLastHour: number;
  turnsLastDay: number;
  recentFeed: { ts: number; userId: string; model: string; mode: string; credits: number; tokens: number; hadScreenshot: boolean }[];
  serviceMix: { service: string; calls: number; costUsd: number }[];
};
export const useObservability = () => useAdmin<ObsReport>("/admin/observability");

export type ProviderHealth = {
  id: string; label: string; enabled: boolean; status: "up" | "down" | "paused" | "unknown";
  lastTs: number; latencyMs: number; statusCode: number | null; error: string | null;
  uptimePct: number | null; avgLatencyMs: number | null; samples24h: number;
};
export function useProviderHealth(): { providers: ProviderHealth[] | null; reload: () => void } {
  const [providers, setProviders] = useState<ProviderHealth[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ providers: ProviderHealth[] }>("/admin/provider-health")
      .then((d) => alive && setProviders(d.providers))
      .catch(() => alive && setProviders(null));
    return () => { alive = false; };
  }, [nonce]);
  return { providers, reload: () => setNonce((n) => n + 1) };
}

// ---- whole memory store (/admin/memory/all) ---------------------------------
export type AdminMemory = { id: string; user: string; kind: string; text: string; conf: number };
export function useMemoryAll(): { entries: AdminMemory[] | null; reload: () => void } {
  const [entries, setEntries] = useState<AdminMemory[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ entries: AdminMemory[] }>("/admin/memory/all")
      .then((d) => alive && setEntries(d.entries))
      .catch(() => alive && setEntries(null));
    return () => { alive = false; };
  }, [nonce]);
  return { entries, reload: () => setNonce((n) => n + 1) };
}
export const deleteMemory = (id: string) => post("/admin/memory-delete", { id });

// ---- one user's memories (/admin/memory?user=<id>) --------------------------
export type UserMemoryEntry = { kind: string; text: string; conf: number };
export function useUserMemory(userId: string): UserMemoryEntry[] | null {
  const [data, setData] = useState<UserMemoryEntry[] | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ byKind: Record<string, { value: string; confidence: number }[]> }>(`/admin/memory?user=${encodeURIComponent(userId)}`)
      .then((d) => {
        const entries: UserMemoryEntry[] = [];
        for (const [kind, items] of Object.entries(d.byKind ?? {})) {
          for (const it of items) entries.push({ kind, text: it.value, conf: it.confidence });
        }
        if (alive) setData(entries);
      })
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [userId]);
  return data;
}
